import type { Schemas } from '../api/client';

type Task = Schemas['TaskResponse'];

const DAY = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: 'long', timeZone: 'UTC' });

export const asDate = (d: string) => new Date(`${d}T00:00:00Z`);
export const formatDay = (d: string) => DAY.format(asDate(d));
export const formatWeekday = (d: string) => WEEKDAY.format(asDate(d));

const PART: Record<string, string> = { MORNING: 'morning', AFTERNOON: 'afternoon', EVENING: 'evening' };

/** Human due-date line. Flexible occurrences show their window and preferred day (docs/recurring-tasks.md §1.2). */
export function dueLabel(task: Task): string | null {
  const part = task.duePartOfDay ? ` · ${PART[task.duePartOfDay]}` : '';
  if (task.timing === 'FLEXIBLE' && task.dueOn) {
    const ideally = task.preferredOn ? ` · ideally ${formatWeekday(task.preferredOn)}` : '';
    return task.overdue ? `Overdue · was due by ${formatDay(task.dueOn)}` : `By ${formatDay(task.dueOn)}${ideally}${part}`;
  }
  if (!task.dueOn) return null;
  if (task.overdue) return `Overdue · ${formatDay(task.dueOn)}${part}`;
  if (task.dueToday) return `Today${task.timing === 'FIXED_DAY' ? ' (set day)' : ''}${part}`;
  return `${formatDay(task.dueOn)}${task.timing === 'FIXED_DAY' ? ' (set day)' : ''}${part}`;
}

export function priorityTone(code: string): 'neutral' | 'info' | 'warn' | 'danger' {
  return code === 'URGENT' ? 'danger' : code === 'HIGH' ? 'warn' : code === 'LOW' ? 'neutral' : 'info';
}

export function timeAgo(iso: string | Date): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d} d ago` : new Date(iso).toLocaleDateString();
}

/** ISO weekday the household week starts on, from the browser locale (decision Q-R7). */
export function localeWeekStart(): 1 | 7 {
  try {
    const info = (new Intl.Locale(navigator.language) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } });
    const firstDay = info.getWeekInfo?.().firstDay ?? info.weekInfo?.firstDay ?? 1;
    return firstDay === 7 ? 7 : 1;
  } catch {
    return 1;
  }
}

export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export const WEEKDAY_LABEL: Record<(typeof WEEKDAYS)[number], string> = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

export function scheduleSummary(s: Schemas['ScheduleResponse']): string {
  const every = (unit: string) => (s.interval === 1 ? `Every ${unit}` : `Every ${s.interval} ${unit}s`);
  const days = s.weekdays.map((d) => WEEKDAY_LABEL[d as (typeof WEEKDAYS)[number]]).join(', ');
  if (s.frequency === 'DAILY') return every('day');
  if (s.frequency === 'WEEKLY') {
    if (s.timing === 'FIXED_DAY') return `${every('week')} on ${days}`;
    return `${every('week')}, any day${days ? ` (ideally ${days})` : ''}`;
  }
  if (s.frequency === 'YEARLY') {
    const month = MONTHS[(s.monthOfYear ?? 1) - 1];
    const date = s.monthDay === -1 ? `the last day of ${month}` : s.monthDay ? `${month} ${s.monthDay}` : null;
    return s.timing === 'FIXED_DAY' ? `${every('year')} on ${date}` : `${every('year')}, any day in ${month}${date ? ` (ideally ${date})` : ''}`;
  }
  const day = s.monthDay === -1 ? 'the last day' : s.monthDay ? `day ${s.monthDay}` : null;
  return s.timing === 'FIXED_DAY' ? `${every('month')} on ${day}` : `${every('month')}, any day${day ? ` (ideally ${day})` : ''}`;
}
